import type { SearchHit } from "@beacon/core/projects"
import { answers, ticketJumpOf } from "./describe.ts"
import type { Destination } from "./destinations.ts"

/*
 * What the command palette offers for what is typed, and in what order, apart from how it draws
 * it: the groups in order on each layout, the pages under Go to, the people a name finds, the words
 * worth asking the web API's ticket search about, and the tickets it found.
 */

export type PaletteGroup =
  "Jump to ticket" | "Go to" | "Projects" | "People" | "Tickets" | "Actions"

/**
 * The groups in the order they render. A typed key's jump always leads. On the desk, Go to and
 * the Projects come first, as they always have. On `compact`, where the tab bar already holds the
 * pages Members go to most, the Member's Projects lead and Go to follows them. People hold rows
 * only once something is typed, and are found as it is typed. Tickets come last on both layouts:
 * they arrive from the web API a moment later, and the Combobox keeps its highlight by position,
 * so rows arriving above the highlighted one would hand the highlight to another row.
 */
export function groupsOf(compact: boolean): readonly PaletteGroup[] {
  return compact
    ? ["Jump to ticket", "Projects", "People", "Go to", "Actions", "Tickets"]
    : ["Jump to ticket", "Go to", "Projects", "People", "Actions", "Tickets"]
}

/**
 * Runs a chosen row and closes the palette. On the desk it closes first. On `compact` the palette
 * is a sheet whose close takes its history entry back off, which cancels a page that loads lazily
 * if it hasn't arrived yet, so there it closes once `run` has settled, whether or not it failed.
 * The returned promise settles as `run` does.
 */
export function chooseIn(compact: boolean, run: () => void | Promise<void>, close: () => void) {
  if (!compact) close()
  const ran = new Promise<void>((resolve) => resolve(run()))
  return compact ? ran.finally(close) : ran
}

/**
 * The pages offered under Go to. An empty search on `compact` leaves out those with a tab of their
 * own, since the tab bar under Search already offers them; typing a page's name still finds it.
 */
export function goToOf(
  pages: readonly Destination[],
  { compact, query }: { compact: boolean; query: string },
) {
  return compact && !query.trim() ? pages.filter(({ tab }) => !tab) : pages
}

/** The most people a search lists, so a common first name doesn't push the tickets out of sight. */
const peopleShown = 5

/**
 * The Members a search names: every word in their name, in any case, those whose name starts with
 * what is typed first, then by name. A deactivated Member is left out, as are all of them for an
 * empty search.
 */
export function peopleAnswering<Person extends { name: string; deactivatedAt: string | null }>(
  query: string,
  people: readonly Person[],
) {
  const typed = query.trim().toLocaleLowerCase()
  if (!typed) return []
  const leads = (person: Person) => person.name.toLocaleLowerCase().startsWith(typed)
  return people
    .filter((person) => person.deactivatedAt === null && answers(typed, [person.name]))
    .toSorted((a, b) => Number(leads(b)) - Number(leads(a)) || a.name.localeCompare(b.name))
    .slice(0, peopleShown)
}

/** The fewest characters worth a ticket search: one letter finds too much to read. */
const fewestSearched = 2

/**
 * The words to ask the ticket search about, or none: nothing for fewer than two characters, nor
 * for a ticket's key, which the jump already opens.
 */
export function ticketWordsOf(query: string) {
  const words = query.trim()
  if (words.length < fewestSearched || ticketJumpOf(words)) return undefined
  return words
}

/**
 * The tickets a search found, while what is typed is still worth searching: an answer kept from
 * before is not shown for a ticket's key, nor once the field is cleared.
 */
export function ticketsShown(hits: readonly SearchHit[] | undefined, query: string) {
  return hits && ticketWordsOf(query) ? hits : []
}

/**
 * Whether the ticket search still owes an answer to what is typed: the Member hasn't paused on it
 * yet (`settled` is the words last searched for), or the search for it is on its way. While it
 * does, a palette with no rows says it is searching rather than that nothing matches.
 */
export function ticketsPending(query: string, settled: string | undefined, fetching: boolean) {
  const words = ticketWordsOf(query)
  return words !== undefined && (words !== settled || fetching)
}
